/* RESPONSIBLE TEAM: team-inbox */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-bare-strings */
import Service, { inject as service } from '@ember/service';
import type Session from './session';
import { get, put } from 'embercom/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { type NexusEvent } from 'embercom/services/nexus';
import type Nexus from 'embercom/services/nexus';
import { NexusEventName, NexusStatusEvents } from 'embercom/services/nexus';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import storage from 'embercom/vendor/intercom/storage';
import type Inbox2AssigneeSearch from './inbox2-assignee-search';
import { type ChannelAvailabilityCode } from './inbox2-teammate-activity-service';
import type LbaMetricsService from 'embercom/services/lba-metrics-service';
import { LbaTriggerEvent } from 'embercom/services/lba-metrics-service';
import { later, cancel } from '@ember/runloop';
import ENV from 'embercom/config/environment';
import type LogService from 'embercom/services/log-service';
import { type AwayStatusReason } from 'embercom/objects/inbox/away-reason';
import HasFeatureHelper from 'embercom/helpers/has-feature';

interface AwaySettingsWireFormat {
  away_status_reason: string;
  away_status_reason_id: string;
  away_mode_enabled: boolean;
  reassign_conversations: boolean;
  default_assignee_id: number;
  is_auto_changed: boolean;
  auto_changed_time: any;
  channel_availability: string;
  auto_pause_on_away_enabled: boolean;
}

enum SupportedOperations {
  ToggleAwayMode,
  TogglereassignConversations,
  SetAwayReason,
  SetAvailableChannel,
  SetAvailableChannelForTeammate,
  SetAwayModeFor,
}

export enum PhoneAwayStatusReasonTypes {
  OnACall = 'on_a_call',
  WrapUp = 'wrap_up',
  Coaching = 'coaching',
}

export const HAS_CHANGED_OWN_STATUS_KEY = 'has_changed_own_away_status';
export const SILENCE_AWAY_WARNING_KEY = 'silence_away_mode_warning';

export default class AdminAwayService extends Service {
  @service declare session: Session;
  @service declare nexus: Nexus;
  @service declare intl: IntlService;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare notificationsService: any;
  @service declare intercomEventService: any;
  @service declare trackedLocalStorage: any;
  @service declare lbaMetricsService: LbaMetricsService;
  @service declare logService: LogService;

  @tracked awayStatusReason?: AwayStatusReason | null;
  @tracked awayModeEnabled?: boolean;
  @tracked reassignConversations?: boolean;
  @tracked defaultAssigneeId?: number;
  // Tracks whether the current away status was system-initiated (e.g. phone wrap-up).
  // Set to false on any manual admin action — manual changes override automatic ones,
  // so the CallingWrapUpWorker should not clear status the admin manually adjusted.
  // See intercom#497049 for context.
  @tracked isAutoChanged?: boolean;
  @tracked autoChangedTime: any;
  @tracked displayModalPrompt = false;
  @tracked channelAvailability = 'conversations';
  @tracked autoPauseOnAwayEnabled = false;
  @tracked _awayStatusReasons: AwayStatusReason[] = [];
  @tracked isLoadingReasons = false;

  private nexusListener?: (e: NexusEvent) => void;

  private refreshTimer: any;
  // @ts-ignore
  hasFeatureHelper = HasFeatureHelper.create(this);

  constructor() {
    super(...arguments);

    // We rely on Nexus + polling to keep the away status in sync.
    //
    // Nexus is used for real time updates.
    //
    // Polling is used as a fallback to Nexus in case Nexus events are lost (Nexus is known to have some reliability issues [1]).
    //
    // [1] https://github.com/intercom/intercom/issues/355592#issuecomment-2358477226
    this.subscribe();
    this.nexus.on(NexusStatusEvents.Reconnected, () => this.getAwaySettings());
    this.scheduleRefresh();
    this.getAwaySettings();
    this.setup();
  }

  async setup() {
    if (this.awayStatusReasons.length === 0 && !this.isLoadingReasons) {
      await this.fetchAwayStatusReasons();
    }
  }

  get awayStatusReasons(): AwayStatusReason[] {
    return this._awayStatusReasons;
  }

  get phoneAwayStatusReasons(): AwayStatusReason[] {
    return this._awayStatusReasons.filter((reason) => reason.associatedToPhoneStatus);
  }

  async findAwayStatusReasonForPhone(reasonType: PhoneAwayStatusReasonTypes) {
    if (this.awayStatusReasons.length === 0) {
      await this.fetchAwayStatusReasons();
    }
    return this.phoneAwayStatusReasons.find(
      (reason) => reason.associatedToPhoneStatus === reasonType,
    );
  }

  isCurrentStatusAssociatedToPhoneStatus(targetType: PhoneAwayStatusReasonTypes) {
    if (!this.awayStatusReason) {
      return false;
    }

    return this.awayStatusReason.associatedToPhoneStatus === targetType;
  }

  async fetchAwayStatusReasons(force = false) {
    try {
      this.isLoadingReasons = true;
      this._awayStatusReasons = await this.session.workspace.fetchAwayStatusReasons(force);
      if (this.awayStatusReason) {
        let currentReason = this._awayStatusReasons.findBy('id', this.awayStatusReason.id);
        if (currentReason) {
          this.awayStatusReason = currentReason;
        }
      }
    } finally {
      this.isLoadingReasons = false;
    }
  }

  willDestroy() {
    if (this.refreshTimer) {
      cancel(this.refreshTimer);
    }
  }

  subscribe() {
    this.nexusListener = (e: NexusEvent) => {
      this.handleAdminStatusChange(e);
    };
    this.nexus.addListener(NexusEventName.AdminAwayReassignModeChangeEvent, this.nexusListener);
  }

  // This method is used to update the away mode state without saving it to the backend
  // This is useful when we want to update the away mode state for the current admin from the admin list
  // in settings section, where the state is saved via ember-data, but we still want to update the state of user menu
  updateAwayModeStateWithoutSaving(
    awayModeEnabled: boolean,
    reassignConversations: boolean,
    awayStatusReasonId?: string,
  ) {
    this.awayModeEnabled = awayModeEnabled;
    this.reassignConversations = reassignConversations;
    if (awayModeEnabled && awayStatusReasonId) {
      this.awayStatusReason = this.awayStatusReasons.findBy('id', awayStatusReasonId);
    } else {
      this.awayStatusReason = null;
    }
  }

  toggleAwayMode() {
    this.awayModeEnabled = !this.awayModeEnabled;
    this.reassignConversations = false;
    this.awayStatusReason = null;
    this.isAutoChanged = false;
    this.setAwaySettings(SupportedOperations.ToggleAwayMode, this.currentTeammateContext);
  }

  setAdminAsAway(awayStatusReason: AwayStatusReason | null = null) {
    this.awayModeEnabled = true;
    this.awayStatusReason = awayStatusReason;
    this.isAutoChanged = false;
    this.setAwaySettings(SupportedOperations.ToggleAwayMode, this.currentTeammateContext);
  }

  async setAdminAsOnACall() {
    let reason = await this.findAwayStatusReasonForPhone(PhoneAwayStatusReasonTypes.OnACall);
    this.setAdminAsAway(reason);
  }

  async setAdminAsCoaching() {
    let reason = await this.findAwayStatusReasonForPhone(PhoneAwayStatusReasonTypes.Coaching);
    this.setAdminAsAway(reason);
  }

  setAdminAsAvailable() {
    this.awayModeEnabled = false;
    this.awayStatusReason = null;
    this.isAutoChanged = false;
    this.setAwaySettings(SupportedOperations.ToggleAwayMode, this.currentTeammateContext);
  }

  setAwayModeFor(
    teammate_id: number,
    away_mode_enabled: boolean,
    reassign = false,
    away_status_reason: AwayStatusReason | null = null,
  ) {
    if (teammate_id === this.session.teammate.id) {
      this.awayModeEnabled = away_mode_enabled;
      this.reassignConversations = reassign;
      this.awayStatusReason = away_status_reason;
      return this.setAwaySettings(SupportedOperations.SetAwayModeFor, this.currentTeammateContext);
    }

    let context = {
      app_id: this.session.workspace.id,
      id: teammate_id,
      reassign_conversations: reassign,
      away_status_reason_id: away_status_reason?.id,
      away_mode_enabled,
    };
    return this.setAwaySettings(SupportedOperations.SetAwayModeFor, context);
  }

  async bulkSetAwayMode(
    adminIds: number[],
    awayModeEnabled: boolean,
    reassign = false,
    awayStatusReason: AwayStatusReason | null = null,
  ) {
    await put('/ember/admins/bulk_change_away_mode', {
      app_id: this.session.workspace.id,
      admin_ids: adminIds,
      away_mode_enabled: awayModeEnabled,
      reassign_conversations: reassign,
      away_status_reason_id: awayStatusReason?.id,
    });
  }

  toggleAwayModeFor(teammate_id: number, away_mode_enabled: boolean) {
    if (teammate_id === this.session.teammate.id) {
      return away_mode_enabled ? this.setAdminAsAway() : this.setAdminAsAvailable();
    }
    let context = {
      app_id: this.session.workspace.id,
      id: teammate_id,
      reassign_conversations: false,
      away_status_reason: null,
      away_mode_enabled,
    };
    this.setAwaySettings(SupportedOperations.ToggleAwayMode, context);
  }

  toggleReassignConversations() {
    this.reassignConversations = !this.reassignConversations;
    this.isAutoChanged = false;
    this.setAwaySettings(
      SupportedOperations.TogglereassignConversations,
      this.currentTeammateContext,
    );
  }

  toggleReassignConversationsFor(teammate_id: number, reassign: boolean) {
    let context = {
      app_id: this.session.workspace.id,
      id: teammate_id,
      reassign_conversations: reassign,
      away_mode_enabled: true,
    };
    this.setAwaySettings(SupportedOperations.ToggleAwayMode, context);
  }

  setAwayReason(reason: AwayStatusReason) {
    this.awayStatusReason = reason;
    this.intercomEventService.trackAnalyticsEvent({
      action: `Away status reason changed to ${reason}`,
      object: 'away_status_reason_setting',
      context: 'from_profile',
    });
    this.setAwaySettings(SupportedOperations.SetAwayReason, this.currentTeammateContext);
  }

  // There are issues where ember misses admin away status changes triggered from the backend during call wrap up
  // This method is used to force backend consistency in cases where wrap up's away status should have been cleared but was not
  async forceAdminConsistency(wrapUpTimeAmount: number) {
    this.getAwaySettings();

    let consistencyBuffer = 5_000;
    let wrapUpDurationMs = wrapUpTimeAmount * 1_000 + consistencyBuffer;

    if (!this.awayModeEnabled) {
      return;
    }

    let intervalledReload = setInterval(() => {
      this.getAwaySettings();
    }, consistencyBuffer);

    setTimeout(() => {
      clearInterval(intervalledReload);
    }, wrapUpDurationMs);
  }

  async toggleAvailableChannel(value: string) {
    this.channelAvailability = value;
    await this.setAvailableChannel(
      SupportedOperations.SetAvailableChannel,
      this.currentAvailableContext,
    );
  }

  async toggleAvailabeChannelForTeammate(adminId: number, value: ChannelAvailabilityCode) {
    if (adminId === this.session.teammate.id) {
      return this.toggleAvailableChannel(value);
    }

    let context = {
      app_id: this.session.workspace.id,
      id: adminId,
      channel_availability: value,
    };
    await this.setAvailableChannelForTeammate(context);
  }

  async getAwaySettings() {
    let data: AwaySettingsWireFormat = await get(
      `/ember/admins/away_settings.json?app_id=${this.session.workspace.id}`,
    );

    if (!data.away_status_reason_id) {
      this.awayStatusReason = null;
    } else {
      this.getCustomAwayReason(data);
    }

    this.awayModeEnabled = data.away_mode_enabled;
    this.defaultAssigneeId = data.default_assignee_id;
    this.reassignConversations = data.reassign_conversations;
    this.isAutoChanged = data.is_auto_changed;
    this.autoChangedTime = data.auto_changed_time;
    this.channelAvailability = data.channel_availability;
    this.autoPauseOnAwayEnabled = data.auto_pause_on_away_enabled;
  }

  async getCustomAwayReason(data: AwaySettingsWireFormat) {
    // As we use the findBy what we currently have, we need to make sure the away status reasons are already loaded
    if (this.awayStatusReasons.length === 0) {
      await this.fetchAwayStatusReasons();
    }
    this.awayStatusReason = this.awayStatusReasons.findBy(
      'id',
      data.away_status_reason_id.toString(),
    );
  }

  async setAwaySettings(kind: SupportedOperations, context: any = {}) {
    try {
      if (context.id === this.session.teammate.id) {
        this.trackTeammateComingBackOnline(kind, context);
        this.trackedLocalStorage.setItem(HAS_CHANGED_OWN_STATUS_KEY, '1');
        await put(`/ember/admins/change_own_away_mode`, context);
      } else {
        await put(`/ember/admins/change_away_mode`, context);
      }
    } catch (e: unknown) {
      this.revertCurrentTeammateStatus(kind, context);
      let status = (e as { jqXHR?: { status?: number } })?.jqXHR?.status;
      let isPermissionError =
        status === 403 &&
        this.session.workspace.isFeatureEnabled('spg-inbox-reassign-conversations-on-away');
      if (isPermissionError) {
        this.notificationsService.notifyError(
          this.intl.t('inbox.user-menu.reassign-permission-error'),
        );
      } else {
        this.notificationsService.notifyError(this.intl.t('inbox.user-menu.set-status-error'));
      }
      throw e;
    }
  }

  async setAvailableChannel(kind: SupportedOperations, context: any = {}) {
    try {
      await put(`/ember/admins/change_active_channel`, context);
    } catch (e) {
      this.revertCurrentTeammateStatus(kind, context);
      this.notificationsService.notifyError(this.intl.t('inbox.user-menu.set-status-error'));
      throw e;
    }
  }

  async setAvailableChannelForTeammate(context: {
    app_id: string;
    id: number;
    channel_availability: ChannelAvailabilityCode;
  }) {
    try {
      await put(`/ember/admins/change_teammate_active_channel`, context);
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.dashboard.teammate-activity.table.set-status-error'),
      );
      throw e;
    }
  }

  getTranslationKeyForDefaultAwayReason(reason: AwayStatusReason) {
    let map: { [reason: string]: string } = {
      Coaching: 'coaching',
      'On a break': 'on-a-break',
      'On lunch': 'on-lunch',
      'In a meeting': 'in-a-meeting',
      'On a call': 'on-a-call',
      'Call wrap up': 'call-wrap-up',
      'Done for the day': 'done-for-the-day',
      'Out of office': 'on-holidays',
      'Out sick': 'out-sick',
    };
    if (!Object.keys(map).includes(reason.label)) {
      return;
    }
    return map[reason.label];
  }

  handleAdminStatusChange(e: NexusEvent) {
    if (e.eventData.admin_id !== this.session.teammate.id) {
      let admin = this.inbox2AssigneeSearch.findAdminById(e.eventData.admin_id);
      if (admin) {
        admin.adminStatus = {
          awayModeEnabled: e.eventData.away_mode_enabled,
          reassignConversations: e.eventData.reassign_conversations,
          isAutoChanged: e.eventData.is_auto_changed,
          channelAvailability: e.eventData.channel_availability,
        };
      }
      return;
    }

    // We intentionally do NOT sync isAutoChanged or channelAvailability here.
    // Any manual admin action (toggle away, change reason, toggle reassign) sets
    // isAutoChanged = false locally, and that manual override takes precedence over
    // the backend's auto_changed state. Syncing it would let the backend overwrite
    // the admin's manual intent.
    this.awayModeEnabled = e.eventData.away_mode_enabled;
    this.reassignConversations = e.eventData.reassign_conversations;
  }

  @action maybeDisplayModalPrompt() {
    if (storage.get(SILENCE_AWAY_WARNING_KEY)) {
      return;
    }

    if (!this.awayModeEnabled) {
      return;
    }

    this.displayModalPrompt = true;
  }

  @action silenceAwayModePrompt() {
    storage.set(SILENCE_AWAY_WARNING_KEY, true);
  }

  scheduleRefresh() {
    this.refreshTimer = later(this, this.refreshAwaySettings, ENV.APP.awayModeRefreshInterval);
  }

  async refreshAwaySettings() {
    let nexusConnected = this.nexus.isConnected;
    let awayModeEnabledBefore = this.awayModeEnabled;

    await this.getAwaySettings();

    let awayModeEnabledAfter = this.awayModeEnabled;

    // If nexus is connected and the away mode we just fetched does not match the one we had before
    // it means that the AdminAwayReassignModeChange nexus event was lost/not received
    if (nexusConnected && awayModeEnabledBefore !== awayModeEnabledAfter) {
      this.logService.log({
        log_type: 'stale_away_mode_refresh',
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
        away_mode_enabled_before: awayModeEnabledBefore,
        away_mode_enabled_after: awayModeEnabledAfter,
      });
    }

    if (ENV.environment !== 'test') {
      this.scheduleRefresh();
    }
  }

  private revertCurrentTeammateStatus(kind: SupportedOperations, context: any = {}) {
    if (context.id === this.session.teammate.id) {
      switch (kind) {
        case SupportedOperations.ToggleAwayMode: {
          this.awayModeEnabled = !this.awayModeEnabled;
          break;
        }
        case SupportedOperations.TogglereassignConversations: {
          this.reassignConversations = !this.reassignConversations;
          break;
        }
        case SupportedOperations.SetAvailableChannel: {
          this.channelAvailability = 'conversation';
          break;
        }
        case SupportedOperations.SetAwayModeFor: {
          this.awayModeEnabled = !context.away_mode_enabled;
          this.reassignConversations = !context.reassign_conversations;
          if (!this.awayModeEnabled) {
            this.awayStatusReason = null;
          }
          break;
        }
      }
    }
  }

  private get currentAvailableContext() {
    return {
      app_id: this.session.workspace.id,
      id: this.session.teammate.id,
      channel_availability: this.channelAvailability,
    };
  }

  private get currentTeammateContext() {
    // is_auto_changed is intentionally omitted. All frontend-initiated PUTs are manual
    // admin actions, so the backend correctly defaults to auto_changed=false.
    // The CallingWrapUpWorker uses auto_changed to distinguish system-initiated
    // wrap-up from admin-initiated changes — see intercom#497049.
    let currentSelectedAwayStatusReason = this.awayStatusReason?.id;

    return {
      app_id: this.session.workspace.id,
      id: this.session.teammate.id,
      reassign_conversations: this.reassignConversations,
      away_mode_enabled: this.awayModeEnabled,
      away_status_reason_id: currentSelectedAwayStatusReason,
    };
  }

  private trackTeammateComingBackOnline(
    kind: SupportedOperations,
    context: { away_mode_enabled: any },
  ) {
    if (kind === SupportedOperations.ToggleAwayMode && !context.away_mode_enabled) {
      this.lbaMetricsService.trackTeammateMaybeWaitingForNewConversationAt(LbaTriggerEvent.ONLINE);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    adminAwayService: AdminAwayService;
    'admin-away-service': AdminAwayService;
  }
}
